import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { DialogModule } from 'primeng/dialog';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { MessageModule } from 'primeng/message';

import type { NotificationDto } from 'api';

import { parseCostUpdatedPayload, currencyLabel, type RecalculationTarget } from '../cost-updated-payload';

/**
 * Modal de detalle de una notificación de **costo actualizado** (spec §4). Parsea el `payloadJson`
 * y muestra la tabla `lists[]` (lista, costo/precio/margen antes→después, qué se recalculó),
 * resaltando en rojo las filas que quedaron con margen negativo.
 *
 * Es presentacional: recibe la `NotificationDto` y su visibilidad (two-way); no conoce el store ni
 * navega. Si el payload no es de costo (defensivo), cae a mostrar el `body`.
 */
@Component({
  selector: 'app-notification-detail-dialog',
  standalone: true,
  imports: [DecimalPipe, DialogModule, TableModule, TagModule, TooltipModule, MessageModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-dialog
      [(visible)]="visible"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '920px', maxWidth: '95vw' }"
      header="Detalle de actualización de costo"
    >
      @let p = payload();
      @if (p) {
        <div class="mb-4">
          <div class="flex items-center gap-2 flex-wrap">
            <span class="font-semibold text-base">{{ p.itemName }}</span>
            @if (p.sku) {
              <span class="text-xs text-muted-color font-mono">{{ p.sku }}</span>
            }
          </div>
          <div class="flex items-center gap-2 flex-wrap mt-1 text-xs text-muted-color">
            <p-tag [value]="originLabel(p.originDocumentType)" severity="secondary" />
            @if (p.originDocumentNumber) {
              <span>{{ p.originDocumentNumber }}</span>
            }
            <span>·</span>
            <span>Fuente de costo: {{ sourceLabel(p.source) }}</span>
          </div>
        </div>

        @if (p.hasNegativeMargin) {
          <p-message severity="warn" styleClass="mb-3 w-full" [text]="negativeMarginWarning()" />
        }

        <p-table [value]="p.lists" styleClass="p-datatable-sm p-datatable-gridlines">
          <ng-template pTemplate="header">
            <tr>
              <th>Lista</th>
              <th class="text-right">Costo</th>
              <th class="text-right">Precio venta</th>
              <th class="text-right" style="width: 9rem">Margen</th>
              <th style="width: 9rem">Se recalculó</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [class.bg-red-50]="row.negativeMargin" [class.dark:bg-red-950]="row.negativeMargin">
              <td>
                <div class="flex items-center gap-2">
                  <span>{{ row.listPriceName }}</span>
                  @if (currencyLabel(row); as code) {
                    <span class="text-xs text-muted-color">{{ code }}</span>
                  }
                </div>
              </td>
              <td class="text-right tabular-nums whitespace-nowrap">
                <span class="text-muted-color">{{ row.previousCost | number: '1.2-2' }}</span>
                <i class="pi pi-arrow-right mx-1 text-[10px] text-muted-color"></i>
                <span class="font-semibold">{{ row.newCost | number: '1.2-2' }}</span>
              </td>
              <td class="text-right tabular-nums whitespace-nowrap">
                <span class="text-muted-color">{{ row.previousPriceOfSale | number: '1.2-2' }}</span>
                <i class="pi pi-arrow-right mx-1 text-[10px] text-muted-color"></i>
                <span class="font-semibold">{{ row.newPriceOfSale | number: '1.2-2' }}</span>
              </td>
              <td class="text-right tabular-nums whitespace-nowrap">
                @if (row.previousMarginPercentage !== null && row.previousMarginPercentage !== undefined) {
                  <span class="text-muted-color">{{ row.previousMarginPercentage | number: '1.0-2' }}%</span>
                } @else {
                  <span class="text-muted-color">—</span>
                }
                <i class="pi pi-arrow-right mx-1 text-[10px] text-muted-color"></i>
                @if (row.newMarginPercentage === null || row.newMarginPercentage === undefined) {
                  <span class="font-semibold">—</span>
                } @else if (row.negativeMargin) {
                  <span class="font-semibold text-red-500">
                    {{ row.newMarginPercentage | number: '1.0-2' }}%
                    <i class="pi pi-exclamation-triangle ml-1" pTooltip="Venta a pérdida" tooltipPosition="top"></i>
                  </span>
                } @else {
                  <span class="font-semibold">{{ row.newMarginPercentage | number: '1.0-2' }}%</span>
                }
              </td>
              <td>
                <p-tag
                  [value]="recalcLabel(row.recalculationTarget)"
                  [severity]="recalcSeverity(row.recalculationTarget)"
                />
              </td>
            </tr>
          </ng-template>
        </p-table>
      } @else {
        <p class="text-sm text-muted-color py-4">{{ notification()?.body || 'Sin desglose disponible.' }}</p>
      }
    </p-dialog>
  `,
})
export class NotificationDetailDialogComponent {
  /** Notificación cuyo detalle se muestra. */
  public readonly notification = input<NotificationDto | null>(null);
  /** Visibilidad del diálogo (two-way). */
  public readonly visible = model<boolean>(false);

  protected readonly payload = computed(() => parseCostUpdatedPayload(this.notification()?.payloadJson));

  /** Muestra el `currencyCode` del payload (moneda principal del tenant); vacío si el backend no lo envía. */
  protected readonly currencyLabel = currencyLabel;

  protected readonly negativeMarginWarning = computed(() => {
    const count = this.payload()?.lists.filter((l) => l.negativeMargin).length ?? 0;
    return count === 1
      ? 'Una lista quedó con margen negativo (se vende por debajo del costo).'
      : `${count} listas quedaron con margen negativo (se venden por debajo del costo).`;
  });

  protected originLabel(type: string | null | undefined): string {
    switch (type) {
      case 'PurchaseReceipt':
        return 'Recepción de compra';
      case 'InventoryMovement':
        return 'Movimiento de inventario';
      // La guía 2026-07-02 emite 'ManualEdit'; 'Manual' se mantiene por notificaciones antiguas.
      case 'ManualEdit':
      case 'Manual':
        return 'Cambio manual';
      default:
        return type ?? '—';
    }
  }

  protected sourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'AverageCost':
        return 'Costo promedio';
      case 'LastInventoryCost':
        return 'Último costo inv.';
      case 'LastPurchaseCost':
        return 'Última compra';
      case 'Manual':
        return 'Manual';
      default:
        return source ?? '—';
    }
  }

  protected recalcLabel(target: RecalculationTarget | null | undefined): string {
    switch (target) {
      case 'SalePrice':
        return 'Precio de venta';
      case 'Margin':
        return 'Utilidad';
      default:
        return 'Manual';
    }
  }

  protected recalcSeverity(target: RecalculationTarget | null | undefined): 'info' | 'warn' | 'secondary' {
    switch (target) {
      case 'SalePrice':
        return 'info';
      case 'Margin':
        return 'warn';
      default:
        return 'secondary';
    }
  }
}
